import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';


import sliceLogo from '@/assets/slice-logo-v2-rotating.png';
import { getAuthRedirectUrl } from '@/config/urls';

const AuthPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useSupabaseAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  

  // Redirect authenticated users to home (ProtectedRoute will handle routing)
  // Preserve the hash when redirecting - it may contain type=signup for email verification
  useEffect(() => {
    if (!authLoading && user) {
      const hash = window.location.hash;
      console.log('AuthPage: User already authenticated, redirecting to / with hash:', hash);
      navigate('/slice-ai' + hash, { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleResetSession = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
      localStorage.clear();
      sessionStorage.clear();
      toast.success('Session cleared. Please try signing in again.');
      window.location.reload();
    } catch (error) {
      console.error('Error resetting session:', error);
      toast.error('Failed to reset session');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('request-password-reset', {
        body: {
          email: forgotPasswordEmail,
          redirectUrl: getAuthRedirectUrl('/reset-password'),
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('If an account exists with this email, you will receive a password reset link.');
      setForgotPasswordOpen(false);
      setForgotPasswordEmail('');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) {
        // Friendly error messages
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please confirm your email before signing in.');
        } else {
          setError(error.message);
        }
        return;
      }

      if (data.user) {
        // Ensure profile and beta application exist
        await supabase.rpc('ensure_profile_and_beta_application');
        console.log('AuthPage: User signed in successfully');
        toast.success('Welcome back!');
        // Let UserContext load the profile, then Index will handle routing
        navigate('/slice-ai');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    try {
      // Check if email already exists in profiles
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email.toLowerCase())
        .maybeSingle();

      if (existingProfile) {
        setError('An account with this email already exists. Please sign in instead.');
        setLoading(false);
        return;
      }

      // First, create the user with Supabase Auth (without email confirmation)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getAuthRedirectUrl('/role-selection'),
          data: {
            display_name: email.split('@')[0]
          }
        }
      });
      
      if (error) {
        if (error.message.includes('User already registered')) {
          setError('This email is already registered. Please sign in instead.');
        } else {
          setError(error.message);
        }
        return;
      }

      // Check if user was created successfully
      if (data.user) {
        // Ensure profile and beta application exist
        if (data.session) {
          await supabase.rpc('ensure_profile_and_beta_application', {
            _email: email,
            _display_name: email.split('@')[0]
          });
        }
        
        console.log('AuthPage: User signed up successfully, setting pending verification flag');
        
        // Set flag so when user clicks verification email, we know they're verifying
        sessionStorage.setItem('pendingSignupVerification', 'true');
        
        // Just show a toast - don't show the modal prematurely
        toast.success('Account created! Please check your email to verify your account.');
        setError('');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo and Header */}
        <div className="text-center space-y-3">
          <div className="flex flex-col items-center justify-center space-y-3">
            <img src={sliceLogo} alt="TheSlice Logo" className="h-16 w-16 mix-blend-multiply dark:brightness-0 dark:invert" />
            <h1 className="text-4xl font-bold text-foreground tracking-tight">TheSlice</h1>
          </div>
          <p className="text-muted-foreground text-base">One Platform. Endless Possibilities.</p>
        </div>

        {/* Auth Form */}
        <Card className="slice-card shadow-slice-lg border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-center">Get Started</CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/50 p-1 rounded-xl">
                <TabsTrigger 
                  value="signin" 
                  className="rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-sm font-medium"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-sm font-medium"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-4">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-sm font-medium">Email</Label>
                    <Input 
                      id="signin-email" 
                      name="email" 
                      type="email" 
                      placeholder="Enter your email" 
                      required 
                      className="w-full h-11 rounded-xl border-border/50 bg-background focus:border-primary focus:ring-primary" 
                    />
                  </div>
                    <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Input 
                        id="signin-password" 
                        name="password" 
                        type={showPassword ? 'text' : 'password'} 
                        placeholder="Enter your password" 
                        required 
                        className="w-full h-11 rounded-xl border-border/50 bg-background focus:border-primary focus:ring-primary pr-10" 
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {error && (
                    <Alert className="border-destructive/50 bg-destructive/10 text-destructive rounded-xl">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl" 
                    disabled={loading}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign In
                  </Button>
                </form>

                <div className="text-center">
                  <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
                    <DialogTrigger asChild>
                      <Button variant="link" className="text-sm text-primary hover:text-primary/80">
                        Forgot Password?
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                      <DialogHeader>
                        <DialogTitle>Reset Your Password</DialogTitle>
                        <DialogDescription>
                          Enter your email address and we'll send you a link to reset your password.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="forgot-email">Email</Label>
                          <Input
                            id="forgot-email"
                            type="email"
                            placeholder="Enter your email"
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                            required
                            disabled={forgotPasswordLoading}
                            className="h-11 rounded-xl"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full h-11 bg-primary hover:bg-primary/90 rounded-xl" 
                          disabled={forgotPasswordLoading}
                        >
                          {forgotPasswordLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            'Send Reset Link'
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </TabsContent>

              <TabsContent value="signup" className="py-6 flex flex-col items-center text-center space-y-5">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-7 w-7 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-foreground">Join TheSlice</h3>
                  <p className="text-muted-foreground text-sm max-w-[280px] mx-auto">
                    Create your account on our website to get started with the platform.
                  </p>
                </div>
                <a
                  href="https://theslice.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button 
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl"
                  >
                    Sign Up at theslice.ai
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <p className="text-xs text-muted-foreground">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => {
                      const signinTrigger = document.querySelector('[role="tab"][data-value="signin"]') as HTMLElement;
                      signinTrigger?.click();
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="space-y-3">
          <p className="text-center text-xs text-muted-foreground">
            By continuing, you agree to our{' '}
            <a href="/terms" className="underline hover:text-foreground transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</a>
          </p>
          <button 
            onClick={handleResetSession}
            className="text-center text-xs text-muted-foreground hover:text-foreground underline w-full transition-colors"
          >
            Having trouble? Reset session
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;